import React, { ChangeEvent, useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { useUploadProfileImageMutation } from 'src/api/v2/profile-settings-v2-ApiSlice';
import { ProfileImageType } from 'src/model/api/v2/data-contracts';
import { useAppDispatch } from 'src/app/hooks';
import { showSnackBar } from 'src/api/ui-slice';
import { getFrontendURL } from 'src/utils/url';
import { ts } from 'src/utils/translate';
import { theme } from 'src/theme/mui-theme';

const MAX_IMAGE_SIZE_BYTES = 500 * 1024;
const PREVIEW_WIDTH = 200;
const PREVIEW_HEIGHT = 150;

type Props = {
    type: ProfileImageType;
    fileName: string;
};

export const ProfileImageUpload = ({ type, fileName: initialFileName }: Props) => {
    const dispatch = useAppDispatch();
    const [uploadProfileImage, { isLoading }] = useUploadProfileImageMutation();
    const [fileName, setFileName] = useState(initialFileName);

    const previewSrc = fileName
        ? `${getFrontendURL()}/images/avatars/${fileName}`
        : `${getFrontendURL()}/images/profile_placeholder.png`;

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const image = event.target.files?.[0];
        event.target.value = '';
        if (!image) return;

        if (image.size > MAX_IMAGE_SIZE_BYTES) {
            dispatch(showSnackBar({
                text: ts('pr_file_too_big_500', 'File is too big (Max 500KiB)'),
                severity: 'error',
            }));
            return;
        }

        uploadProfileImage({ type, image })
            .unwrap()
            .then((response) => {
                setFileName(response.file_name);
                dispatch(showSnackBar({
                    text: ts('profile_details_saved', 'Profile details saved!'),
                    severity: 'success',
                }));
            })
            .catch((error) => {
                if (error?.data?.errorMessage == null) {
                    dispatch(showSnackBar({
                        text: ts('profile_settings_save_error', 'Failed to save profile settings'),
                        severity: 'error',
                    }));
                }
            });
    };

    return (
        <Stack spacing={1} alignItems="flex-start">
            <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                    width: PREVIEW_WIDTH,
                    height: PREVIEW_HEIGHT,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    overflow: 'hidden',
                }}
            >
                <Box component="img" src={previewSrc} sx={{ maxWidth: '100%', maxHeight: '100%' }} />
            </Stack>
            <Button variant="outlined" component="label" disabled={isLoading} sx={{ textTransform: 'uppercase' }}>
                {ts('profile_settings_change', 'Change')}
                <input hidden type="file" accept="image/png,image/jpeg" onChange={handleFileChange} />
            </Button>
        </Stack>
    );
};
